import { useEffect, type ReactNode } from "react";
import { matchPath, useLocation, useRouter } from "./lib/router.js";
import { useSession, type SessionUser } from "./lib/session.js";
import { AppShell } from "./components/AppShell.js";
import { PageSpinner } from "./components/ui.js";
import { BootstrapPage } from "./pages/BootstrapPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { AcceptInvitePage } from "./pages/AcceptInvitePage.js";
import { OnboardingWizard } from "./pages/onboarding/OnboardingWizard.js";
import { LifeProfilePage } from "./pages/LifeProfilePage.js";
import { TodayPage } from "./pages/TodayPage.js";
import { QuickLogPage } from "./pages/QuickLogPage.js";
import { RecoveryPage } from "./pages/RecoveryPage.js";
import { ProgressPage } from "./pages/ProgressPage.js";
import { BodyPage } from "./pages/BodyPage.js";
import { NutritionPage } from "./pages/nutrition/NutritionPage.js";
import { GroceryPage } from "./pages/GroceryPage.js";
import { TrainingPage } from "./pages/training/TrainingPage.js";
import { WorkoutModePage } from "./pages/training/WorkoutModePage.js";
import { MorePage } from "./pages/MorePage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { InsightsPage } from "./pages/InsightsPage.js";
import { JournalPage } from "./pages/JournalPage.js";
import { GoalsPage } from "./pages/GoalsPage.js";
import { TimelinePage } from "./pages/TimelinePage.js";
import { NotFoundPage } from "./pages/NotFoundPage.js";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage.js";
import { AdminInvitesPage } from "./pages/admin/AdminInvitesPage.js";
import { AdminAuditPage } from "./pages/admin/AdminAuditPage.js";
import { AdminSystemPage } from "./pages/admin/AdminSystemPage.js";
import { AdminAiPage } from "./pages/admin/AdminAiPage.js";

function Redirect({ to }: { to: string }) {
  const { navigate } = useRouter();
  useEffect(() => {
    navigate(to, { replace: true });
  }, [to, navigate]);
  return <PageSpinner />;
}

interface Route {
  pattern: string;
  element: (params: Record<string, string>) => ReactNode;
  admin?: boolean;
}

const ROUTES: Route[] = [
  { pattern: "/today", element: () => <TodayPage /> },
  // The adaptive daily plan now lives on Today; keep the old path working.
  { pattern: "/plan", element: () => <Redirect to="/today" /> },
  { pattern: "/goals", element: () => <GoalsPage /> },
  { pattern: "/timeline", element: () => <TimelinePage /> },
  { pattern: "/log", element: () => <QuickLogPage /> },
  { pattern: "/progress", element: () => <ProgressPage /> },
  { pattern: "/more", element: () => <MorePage /> },
  { pattern: "/nutrition", element: () => <NutritionPage /> },
  { pattern: "/grocery", element: () => <GroceryPage /> },
  { pattern: "/training", element: () => <TrainingPage /> },
  { pattern: "/workout/:id", element: (params) => <WorkoutModePage id={params.id!} /> },
  { pattern: "/recovery", element: () => <RecoveryPage /> },
  { pattern: "/body", element: () => <BodyPage /> },
  { pattern: "/insights", element: () => <InsightsPage /> },
  { pattern: "/journal", element: () => <JournalPage /> },
  { pattern: "/settings", element: () => <SettingsPage /> },
  { pattern: "/life-profile", element: () => <LifeProfilePage /> },
  { pattern: "/admin", element: () => <AdminUsersPage />, admin: true },
  { pattern: "/admin/users", element: () => <AdminUsersPage />, admin: true },
  { pattern: "/admin/invitations", element: () => <AdminInvitesPage />, admin: true },
  { pattern: "/admin/audit", element: () => <AdminAuditPage />, admin: true },
  { pattern: "/admin/system", element: () => <AdminSystemPage />, admin: true },
  { pattern: "/admin/ai", element: () => <AdminAiPage />, admin: true }
];

function renderAuthedRoute(path: string, user: SessionUser): ReactNode {
  if (path === "/" || path === "/login" || path === "/bootstrap") {
    return <Redirect to="/today" />;
  }
  for (const route of ROUTES) {
    const params = matchPath(route.pattern, path);
    if (params) {
      if (route.admin && user.role !== "admin") {
        return <NotFoundPage />;
      }
      return route.element(params);
    }
  }
  return <NotFoundPage />;
}

export function App() {
  const { loading, user, needsBootstrap } = useSession();
  const path = useLocation();

  if (loading) {
    return <PageSpinner />;
  }

  // Invitation redemption is public (the invitee has no session yet).
  const inviteMatch = matchPath("/invite/:token", path);
  if (inviteMatch && !user) {
    return <AcceptInvitePage token={inviteMatch.token!} />;
  }

  if (!user) {
    if (needsBootstrap) return <BootstrapPage />;
    return <LoginPage />;
  }

  // New users must complete onboarding before reaching the app. The wizard is
  // full-screen (no shell) and autosaves, so this is resumable, not a wall.
  if (user.onboardingComplete === false) {
    return <OnboardingWizard />;
  }

  // A signed-in user landing on an invite link just goes home.
  if (inviteMatch) return <Redirect to="/today" />;

  return <AppShell>{renderAuthedRoute(path, user)}</AppShell>;
}
